import akf from "@App/configs/akf";
import arval from "@App/configs/arval";
import athlon from "@App/configs/athlon";
import autohelden from "@App/configs/autohelden";
import bayerische from "@App/configs/bayerische";
import bayerischeSchadenmeldung from "@App/configs/bayerische-schadenmeldung";
import cpm from "@App/configs/cpm";
import dhl from "@App/configs/dhl";
import dkvMobility from "@App/configs/dkvMobility";
import finn from "@App/configs/finn";
import holman from "@App/configs/holman";
import type { Config } from "@App/configs/interfaces";
import itzehoer from "@App/configs/itzehoer";
import mahle from "@App/configs/mahle";
import mara from "@App/configs/mara";
import meinAuto from "@App/configs/meinAuto";
import mfs from "@App/configs/mfs";
import mfsOpal from "@App/configs/mfsOpal";
import mobileGarantie from "@App/configs/mobileGarantie";
import mobilityConcept from "@App/configs/mobilityConcept";
import myAld from "@App/configs/myald";
import octopusev from "@App/configs/octopusev";
import raiffeisenImpulsDe from "@App/configs/raiffeisenImpulsDe";
import signalIduna from "@App/configs/signalIduna";
import sixt from "@App/configs/sixt";

/**
 * Type representing all uuids of the whitelabel configurations.
 * Add the uuid of the organization to this type when creating a new whitelabel config.
 */
export type WhitelabelKey =
  | "a97b0982-acdc-48e7-b1a8-58d2e30ea195" // arval
  | "f20a6fe7-1b00-4c66-bc32-f3f3f5ca30b3" // athlon
  | "b0b4b640-e67a-4274-a364-8c6b46d2f935" // myald
  | "93ca3527-293f-42fc-8d87-0f6e28b209e7" // cpm
  | "0bbab61f-c0dd-45ea-a7ae-7cf987d333d0" // signal iduna
  | "7c2ad154-df00-4b19-9b07-dd5c33362623" // die bayerische
  | "82f174ff-d8c8-4a1d-ab39-ef7f1d837384" // itzehoer
  | "a81aa3f6-7b57-47c1-97cb-f87e5c293e87" // mahle
  | "d6a96d42-fcfc-43a8-b608-233d634b007b" // mfs
  | "ad2da39f-b683-46be-9022-b838c0a48df8" // raiffeisen impuls de
  | "6e2eb8f4-f0b9-45b1-8ef2-5fa871f3dd58" // sixt
  | "5895ffd2-6acc-4155-8a3b-d1d52373d779" // mobile GARANTIE
  | "bc89391d-9d05-4456-8700-72adbb0f94db" // mfs opal
  | "a62616ef-55d8-4189-a0d7-55e12c9806c9" // MFS Mara
  | "ca5ccbae-575e-4e12-b38c-fea579f5d217" // akf
  | "bba7e0ae-121d-4cb1-9061-663f2bfa3617" // Mein Auto
  | "6ff2d367-729b-4197-a802-8894b84b3a08" // Mobility concept
  | "d9ca9630-f58f-43fd-bad6-e6a7e13f5aa0" // bayerische-schadenmeldung
  | "742690d8-4a71-4cb6-a556-819690afe124" // holman
  | "43d579cf-4778-4f97-aa96-dfe1dc485869" // octopusev
  | "f244f978-7aa4-4f56-a40a-41ae443b0d64" // autohelden
  | "c951995d-9945-4316-9058-3bedf71ce039" // dhl
  | "4a1c4e33-38dc-4cae-9627-67f92e6c6876" // dkv mobility
  | "94d3a139-f7bc-43bb-b48e-c8d88272ac64"; // finn

/**
 * Object containing whitelabel configurations.
 * Maps a unique whitelabel key to its corresponding configuration.
 * Make sure to use a descriptive name for the key, ideally the name of the organization.
 *
 * @satisfies {Record<WhitelabelKey, Partial<Config>>} Ensures the object adheres to the expected structure.
 */
export const whitelabelConfigs = {
  "a97b0982-acdc-48e7-b1a8-58d2e30ea195": arval,
  "f20a6fe7-1b00-4c66-bc32-f3f3f5ca30b3": athlon,
  "b0b4b640-e67a-4274-a364-8c6b46d2f935": myAld,
  "93ca3527-293f-42fc-8d87-0f6e28b209e7": cpm,
  "0bbab61f-c0dd-45ea-a7ae-7cf987d333d0": signalIduna,
  "7c2ad154-df00-4b19-9b07-dd5c33362623": bayerische,
  "82f174ff-d8c8-4a1d-ab39-ef7f1d837384": itzehoer,
  "a81aa3f6-7b57-47c1-97cb-f87e5c293e87": mahle,
  "d6a96d42-fcfc-43a8-b608-233d634b007b": mfs,
  "6e2eb8f4-f0b9-45b1-8ef2-5fa871f3dd58": sixt,
  "ad2da39f-b683-46be-9022-b838c0a48df8": raiffeisenImpulsDe,
  "5895ffd2-6acc-4155-8a3b-d1d52373d779": mobileGarantie,
  "a62616ef-55d8-4189-a0d7-55e12c9806c9": mara,
  "bc89391d-9d05-4456-8700-72adbb0f94db": mfsOpal,
  "ca5ccbae-575e-4e12-b38c-fea579f5d217": akf,
  "bba7e0ae-121d-4cb1-9061-663f2bfa3617": meinAuto,
  "6ff2d367-729b-4197-a802-8894b84b3a08": mobilityConcept,
  "d9ca9630-f58f-43fd-bad6-e6a7e13f5aa0": bayerischeSchadenmeldung,
  "742690d8-4a71-4cb6-a556-819690afe124": holman,
  "43d579cf-4778-4f97-aa96-dfe1dc485869": octopusev,
  "f244f978-7aa4-4f56-a40a-41ae443b0d64": autohelden,
  "c951995d-9945-4316-9058-3bedf71ce039": dhl,
  "4a1c4e33-38dc-4cae-9627-67f92e6c6876": dkvMobility,
  "94d3a139-f7bc-43bb-b48e-c8d88272ac64": finn,
} satisfies Record<WhitelabelKey, Partial<Config>>;
